import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cancelPurchaseSubscription, getPurchases, requestPurchaseRefund } from "../api/purchases";
import { useTranslation } from "../context/TranslationContext";
import ConfirmModal from "../components/ConfirmModal";
import ReasonModal from "../components/ReasonModal";
import Avatar from "../components/ds/Avatar";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";

/**
 * Everything the signed-in user has bought: asking the seller for a refund, and
 * cancelling a subscription.
 *
 * A refund can be asked for once per purchase that charged something — a one-time
 * purchase, or a subscription's first payment. The seller accepts (the money goes back
 * through Keepz) or rejects with a reason, and either way the buyer is notified.
 * Cancelling stops the charges; access runs to the end of the period already paid for.
 * A free trial is listed too, with when it ends; it charged nothing, so it has neither.
 *
 * @see mintly-back/src/services/refundService, mintly-back/src/services/subscriptionService
 */

const money = (amount, currency = "GEL") =>
  `${currency === "GEL" ? "₾" : `${currency} `}${Number(amount || 0).toFixed(2)}`;
const day = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "";
};

/**
 * Where a subscription stands, as one word. A cancelled one keeps its access until the
 * paid period runs out, and only then has ended.
 */
function subscriptionState(subscription) {
  if (!subscription) return null;
  const end = subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;
  const paidUp = end && end > new Date();
  if (subscription.status === "trialing") return "trial";
  if (subscription.status === "active") return "active";
  if (subscription.status === "past_due") return "pastDue";
  if (subscription.status === "canceled" && paidUp) return "canceled";
  if (subscription.status === "canceled" || subscription.status === "revoked") return "ended";
  return null;
}

/** A free trial is still running. Mintly runs those itself: a purchase for 0, until accessUntil. */
const trialRunning = (purchase) =>
  Boolean(purchase.isTrial && purchase.accessUntil && new Date(purchase.accessUntil) > new Date());

/** Where a purchase stands, as one word and a Badge tone. */
function standing(purchase) {
  if (purchase.isTrial) {
    return trialRunning(purchase) ? { key: "trial", tone: "success" } : { key: "trialEnded", tone: "neutral" };
  }
  if (purchase.status === "refunded") return { key: "refunded", tone: "success" };
  if (purchase.refundRequest?.status === "pending") return { key: "requested", tone: "warning" };
  if (purchase.refundRequest?.status === "rejected") return { key: "declined", tone: "danger" };
  const plan = subscriptionState(purchase.subscription);
  if (plan === "trial") return { key: "trial", tone: "success" };
  if (plan === "active") return { key: "active", tone: "success" };
  if (plan === "pastDue") return { key: "pastDue", tone: "warning" };
  if (plan === "canceled" || plan === "ended") return { key: plan, tone: "neutral" };
  return { key: "paid", tone: "success" };
}

export default function PurchasesPage({ isDark, setToast }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  /** The purchase a refund is being asked for. */
  const [asking, setAsking] = useState(null);
  const [sending, setSending] = useState(false);
  /** The purchase whose subscription is being cancelled. */
  const [cancelling, setCancelling] = useState(null);
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getPurchases();
      setPurchases(res?.data || []);
    } catch (err) {
      setToast?.(err?.message || t("purchases.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [setToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const sendRequest = async (reason) => {
    if (!asking) return;
    setSending(true);
    try {
      await requestPurchaseRefund(asking.id, reason);
      setToast?.(t("purchases.requestSent"));
      setAsking(null);
      await load();
    } catch (err) {
      const code = err?.code;
      const known = code && t(`refunds.errors.${code}`) !== `refunds.errors.${code}`;
      setToast?.(known ? t(`refunds.errors.${code}`) : err?.message || t("purchases.requestFailed"));
      if (code === "already_requested") {
        setAsking(null);
        load();
      }
    } finally {
      setSending(false);
    }
  };

  const stopSubscription = async () => {
    const subscription = cancelling?.subscription;
    if (!subscription) return;
    setStopping(true);
    try {
      const res = await cancelPurchaseSubscription(subscription.id);
      const until = day(res?.accessUntil);
      setToast?.(until ? t("purchases.canceled", { date: until }) : t("purchases.canceledNoDate"));
      setCancelling(null);
    } catch (err) {
      const code = err?.code;
      const known = code && t(`purchases.cancelErrors.${code}`) !== `purchases.cancelErrors.${code}`;
      setToast?.(known ? t(`purchases.cancelErrors.${code}`) : err?.message || t("purchases.cancelFailed"));
      if (code === "cannot_cancel" || code === "not_active") setCancelling(null);
    } finally {
      setStopping(false);
      load();
    }
  };

  /** One line on where a subscription's billing stands. */
  const planLine = (subscription, state) => {
    const until = day(subscription.currentPeriodEnd);
    if (state === "trial") return until ? t("purchases.subscription.trialEnds", { date: until }) : "";
    if (state === "active") return until ? t("purchases.subscription.renews", { date: until }) : "";
    if (state === "pastDue") return t("purchases.subscription.pastDue");
    if (state === "canceled") return t("purchases.subscription.accessUntil", { date: until });
    if (state === "ended") {
      // A revoked one stopped when it was revoked, whatever period it had left.
      const ended = day(subscription.status === "revoked" ? subscription.canceledAt : subscription.currentPeriodEnd);
      return ended ? t("purchases.subscription.ended", { date: ended }) : "";
    }
    return "";
  };

  return (
    <Page>
      <PageHeader title={t("purchases.title")} subtitle={t("purchases.subtitle")} />

      {loading ? (
        <p className="text-subheadline text-label-secondary">{t("common.loading")}</p>
      ) : purchases.length === 0 ? (
        <EmptyState
          icon="bag"
          title={t("purchases.empty")}
          message={t("purchases.emptyHint")}
          actionLabel={t("purchases.discover")}
          onAction={() => navigate("/")}
        />
      ) : (
        <ul className="flex flex-col overflow-hidden rounded-radius-lg bg-bg-raised">
          {purchases.map((p) => {
            const state = standing(p);
            const request = p.refundRequest;
            const plan = p.status === "refunded" ? null : subscriptionState(p.subscription);
            const planText = p.isTrial
              ? trialRunning(p)
                ? t("purchases.trialEnds", { date: day(p.accessUntil) })
                : t("purchases.trialEndedOn", { date: day(p.accessUntil) })
              : plan
                ? planLine(p.subscription, plan)
                : "";
            return (
              <li key={p.id} className="flex items-start gap-space-12 border-b border-separator-default px-space-16 py-space-12 last:border-b-0">
                <button
                  type="button"
                  onClick={() => p.mintSpace && navigate(`/mint-space/${p.mintSpace.id}`)}
                  className="shrink-0 rounded-14 outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
                  aria-label={p.mintSpace?.name}
                >
                  <Avatar size="l" src={p.mintSpace?.logo} name={p.mintSpace?.name} />
                </button>

                <div className="flex min-w-0 flex-1 flex-col gap-space-8">
                  <div className="flex flex-wrap items-start justify-between gap-x-space-16 gap-y-space-4">
                    <div className="flex min-w-0 flex-col gap-space-4">
                      <p className="truncate text-headline text-label-primary">{p.productName || t("purchases.untitled")}</p>
                      {p.mintSpace && (
                        <button
                          type="button"
                          onClick={() => navigate(`/mint-space/${p.mintSpace.id}`)}
                          className="max-w-full truncate text-left text-footnote text-label-secondary transition-colors hover:text-accent-text"
                        >
                          {p.mintSpace.name}
                        </button>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-space-4">
                      <p className="text-subheadline-semibold text-label-primary">
                        {money(p.amount, p.currency)}
                        {p.isSubscription && p.period && (
                          <span className="text-footnote text-label-secondary"> / {t(`purchases.period.${p.period}`)}</span>
                        )}
                      </p>
                      <p className="text-footnote text-label-tertiary">{day(p.paidAt)}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-space-8">
                    <Badge size="s" tone={state.tone}>
                      {t(`purchases.status.${state.key}`)}
                    </Badge>
                    {p.subscription?.canCancel ? (
                      <Button variant="gray" size="s" onClick={() => setCancelling(p)}>
                        {t("purchases.cancelSubscription")}
                      </Button>
                    ) : p.canRequestRefund ? (
                      <Button variant="gray" size="s" onClick={() => setAsking(p)}>
                        {t("purchases.requestRefund")}
                      </Button>
                    ) : (
                      p.subscription?.cancelBlockedReason === "unavailable" && (
                        <span className="text-footnote text-label-tertiary">{t("purchases.cancelUnavailable")}</span>
                      )
                    )}
                  </div>

                  {planText && <p className="text-footnote text-label-secondary">{planText}</p>}

                  {request?.status === "pending" && (
                    <p className="text-footnote text-label-secondary">
                      {t("purchases.requestPending", { date: day(request.requestedAt) })}
                    </p>
                  )}
                  {request?.status === "rejected" && (
                    <p className="rounded-radius-sm bg-status-danger-bg px-space-12 py-space-8 text-footnote text-status-danger">
                      {request.decisionReason
                        ? t("purchases.declinedWithReason", { reason: request.decisionReason })
                        : t("purchases.declinedNoReason")}
                    </p>
                  )}
                  {p.status === "refunded" && (
                    <p className="text-footnote text-label-secondary">
                      {t("purchases.refundedNote", { amount: money(p.refundedAmount || p.amount, p.currency) })}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ReasonModal
        isOpen={!!asking}
        onClose={() => !sending && setAsking(null)}
        onConfirm={sendRequest}
        title={t("purchases.requestTitle")}
        message={
          asking
            ? t("purchases.requestMessage", {
                product: asking.productName || t("purchases.untitled"),
                amount: money(asking.amount, asking.currency),
                business: asking.mintSpace?.name || "",
              })
            : ""
        }
        label={t("purchases.reasonLabel")}
        placeholder={t("purchases.reasonPlaceholder")}
        confirmLabel={t("purchases.sendRequest")}
        minLength={3}
        loading={sending}
        isDark={isDark}
      />
      <ConfirmModal
        isOpen={!!cancelling}
        onClose={() => !stopping && setCancelling(null)}
        onConfirm={stopSubscription}
        title={t("purchases.cancelTitle")}
        message={
          cancelling
            ? day(cancelling.subscription?.currentPeriodEnd)
              ? t("purchases.cancelMessage", {
                  product: cancelling.productName || t("purchases.untitled"),
                  date: day(cancelling.subscription.currentPeriodEnd),
                })
              : t("purchases.cancelMessageNoDate", {
                  product: cancelling.productName || t("purchases.untitled"),
                })
            : ""
        }
        confirmLabel={t("purchases.cancelConfirm")}
        cancelLabel={t("purchases.keepSubscription")}
        variant="danger"
        loading={stopping}
        isDark={isDark}
      />
    </Page>
  );
}
